import React, { useState } from "react";
import styles from "./DiscountProductCard.module.css";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartPlus,
  faEye,
  faStar,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { useCart } from "../../context/CartContext";
import {
  incrementProductViewCount,
  likeProduct,
  unLikeProduct,
} from "../../service/product";
import { Modal, ModalBody } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import LoginModal from "../../components/login";
import QuickViewModal from "../../components/QuickViewModal";
import CartSidebar from "../../components/CartSidebar";
import { useNavigate } from "react-router-dom";

const DiscountProductCard = ({ product, featured = false }: any) => {
  const [isHovering, setIsHovering] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showQuickView, setShowQuickView] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { cart, setCart, addToCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  // Safely get the first image or a fallback
  const firstImage = product?.picture?.[0]?.path || "";
  const hasMultipleImages = product?.picture?.length > 1;

  const handleNextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) =>
        prev === product.picture.length - 1 ? 0 : prev + 1
      );
    }
  };
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Calculate prices safely
  const originalPrice = product?.price || 0;
  const ourPrice = product?.ourPrice || 0;
  const discountPercentage = product?.discountPercentage || 0;
  const youSave = originalPrice - ourPrice;

  const isInCart = cart?.some(
    (p: any) => p._id.toString() === product?._id.toString()
  );

  const [isLiked, setIsLiked] = useState(product?.hasUpvoted);
  const { user } = useAuth();

  const toggleLikeProduct = async (productId: any) => {
    try {
      await likeProduct(productId);
      setIsLiked(true);
    } catch (error) {
      console.log("error in liking the product", error);
    }
  };
  const toggleUnlikeProduct = async (productId: any) => {
    try {
      await unLikeProduct(productId);
      setIsLiked(false);
    } catch (error) {
      console.log("error in unliking the product", error);
    }
  };
  const handleAddToCart = (product: any) => {
    addToCart({ ...product, quantity: 1 });
    setIsCartOpen(() => true);
  };
  const handleUpdateQuantity = (id: string, quantity: number) => {
    setCart(
      cart.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };
  const addViewCount = async (productId: any) => {
    await incrementProductViewCount(productId);
  };
  const navigateToProduct = (product: any) => {
    navigate(`/series/${product?.series.id}/product/${product._id}`);
    addViewCount(product._id);
  };
  return (
    <>
      <motion.div
        className={`${styles.productCard} ${
          featured ? styles.featuredCard : ""
        }`}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -5 }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Discount Badge */}
        {discountPercentage > 0 && (
          <motion.div
            className={styles.discountBadge}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.1 }}
          >
            {Math.round(discountPercentage)}% OFF
            <div className={styles.glowEffect}></div>
          </motion.div>
        )}

        {/* Wishlist Button */}
        <motion.button
          className={`${styles.wishlistButton} ${
            isLiked ? styles.inWishlist : ""
          }`}
          onClick={async () =>
            !user
              ? setShowLoginModal(true)
              : isLiked
              ? toggleUnlikeProduct(product?._id)
              : toggleLikeProduct(product?._id)
          }
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FontAwesomeIcon
            icon={faHeart}
            className={isLiked ? styles.wishlistActive : ""}
          />
        </motion.button>

        {/* Product Image */}
        <div className={styles.imageContainer} onClick={handleNextImage}>
          {firstImage ? (
            <img
              src={product.picture[currentImageIndex]?.path || firstImage}
              alt={product?.name || "Product image"}
              className={styles.productImage}
              loading="lazy"
            />
          ) : (
            <div className={styles.imagePlaceholder}>No Image Available</div>
          )}

          {/* Quick View Overlay */}
          {isHovering && (
            <motion.div
              className={styles.quickView}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <button
                className={styles.quickViewBtn}
                onClick={() => setShowQuickView(true)}
              >
                <FontAwesomeIcon icon={faEye} /> Quick View
              </button>
            </motion.div>
          )}
        </div>

        {/* Product Info */}
        <div className={styles.productInfo}>
          <h3
            className={styles.productName}
            onClick={() => navigateToProduct(product)}
          >
            {product?.name || "Untitled Product"}
          </h3>
          <p className={styles.productSeries}>{product?.series?.title || ""}</p>

          {/* Price Display */}
          <div className={styles.priceContainer}>
            {originalPrice > ourPrice && (
              <span className={styles.originalPrice}>
                ₹{originalPrice.toFixed(2)}
              </span>
            )}
            <span className={styles.discountedPrice}>
              ₹{ourPrice.toFixed(2)}
            </span>
            {youSave > 0 && (
              <span className={styles.youSave}>
                You save ₹{youSave.toFixed(2)}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <motion.button
              className={`${styles.addToCartBtn} ${
                isInCart ? styles.inCart : ""
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                isInCart
                  ? removeFromCart(product?._id)
                  : handleAddToCart(product);
              }}
            >
              <FontAwesomeIcon icon={faCartPlus} />{" "}
              {isInCart ? "Remove from Cart" : "Add to Cart"}
            </motion.button>
          </div>

          {/* Popularity Indicator */}
          {product?.upvoteCount > 0 && (
            <div className={styles.popularity}>
              <FontAwesomeIcon icon={faStar} className={styles.starIcon} />
              <span>{product.upvoteCount} people love this</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Quick View Modal */}
      <QuickViewModal
        product={product}
        series={product?.series}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
        onAddToCart={addToCart}
      />
      {showLoginModal && (
        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
          <ModalBody style={{ padding: "0px", margin: "0px" }}>
            <LoginModal isStandalone={true} />
          </ModalBody>
        </Modal>
      )}
      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
      />
    </>
  );
};

export default DiscountProductCard;
